import { TimesheetWithEntry, TimesheetService } from '@umss/shared/data-access';
import {
  ApplicationRef,
  EnvironmentInjector,
  createComponent,
  inject,
  Injectable
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { TimesheetModalComponent } from '../components/timesheet-modal/timesheet-modal.component';
import { ToastService } from './toast.service';

/**
 * Service to dynamically open the TimesheetModalComponent as a modal dialog.
 * Handles component lifecycle, DOM management, and result handling.
 */
@Injectable({ providedIn: 'root' })
export class TimesheetModalService {
  private appRef = inject(ApplicationRef);
  private environmentInjector = inject(EnvironmentInjector);
  private timesheetService = inject(TimesheetService);
  private toastService = inject(ToastService);

  /**
   * Opens the timesheet modal dialog with the provided timesheet data.
   *
   * @param timesheet The timesheet data to display in the modal
   * @param isLoadingInitialEntry Whether the timesheet data is still loading
   * @returns Promise that resolves with the user's action:
   *          - 'approved': User approved the timesheet
   *          - 'rejected': User rejected the timesheet
   *          - undefined: User cancelled/closed the dialog
   *
   * @example
   * const result = await this.timesheetModalService.open(timesheetData);
   * if (result === 'approved') {
   *   // Handle approval
   * } else if (result === 'rejected') {
   *   // Handle rejection
   * }
   */
  open(
    timesheet: TimesheetWithEntry,
    isLoadingInitialEntry: boolean = false
  ): Promise<'rejected' | 'approved' | undefined> {
    return new Promise((resolve) => {
      const hostEl = document.createElement('div');
      document.body.appendChild(hostEl);
      let settled = false;

      // Create the component dynamically with the provided environment injector
      const ref = createComponent(TimesheetModalComponent, {
        environmentInjector: this.environmentInjector,
        hostElement: hostEl
      });

      // Set the required inputs
      ref.setInput('open', true);
      ref.setInput('timesheet', timesheet);
      ref.setInput('isLoadingInitialEntry', isLoadingInitialEntry);

      // Attach the view to the Angular application
      this.appRef.attachView(ref.hostView);
      ref.changeDetectorRef.detectChanges();

      // Helper function to close the dialog and clean up resources
      const closeDialog = (result?: 'rejected' | 'approved'): void => {
        if (settled) return;

        settled = true;
        resolve(result);

        // Clean up: detach view, destroy component, remove DOM element
        this.appRef.detachView(ref.hostView);
        ref.destroy();
        hostEl.remove();
      };

      // Listen to the modal's closed output event
      ref.instance.closed.subscribe((result) => {
        closeDialog(result);
      });
    });
  }

  /**
   * Opens the timesheet modal dialog by fetching entry data from the API.
   * The modal will show immediately with a loading state while fetching shift and shift need data.
   *
   * @param entryId The ID of the timesheet entry to fetch and display
   * @returns Promise that resolves with the user's action:
   *          - 'approved': User approved the timesheet
   *          - 'rejected': User rejected the timesheet
   *          - undefined: User cancelled/closed the dialog or entry failed to load
   *
   * @example
   * const result = await this.timesheetModalService.openById(entryId);
   * if (result === 'approved') {
   *   this.refreshList();
   * } else if (result === 'rejected') {
   *   this.refreshList();
   * }
   */
  async openById(
    entryId: string
  ): Promise<'rejected' | 'approved' | undefined> {
    return new Promise<'rejected' | 'approved' | undefined>((resolve) => {
      const hostEl = document.createElement('div');
      document.body.appendChild(hostEl);
      let settled = false;

      // Create a dummy timesheet object for initial display
      const dummyTimesheet: TimesheetWithEntry = {
        id: entryId,
        timesheetNumberDisplay: 'Loading...',
        timesheetStatus: 'Pending',
        contractorName: 'Loading...',
        facilityName: 'Loading...',
        shiftDate: new Date().toISOString(),
        startTime: '',
        endTime: '',
        hoursWorked: 0,
        hourlyRate: 0,
        lineTotal: 0,
        entryId: entryId,
        timesheetId: '',
        shiftId: ''
      } as unknown as TimesheetWithEntry;

      // Create the component dynamically with loading state
      const ref = createComponent(TimesheetModalComponent, {
        environmentInjector: this.environmentInjector,
        hostElement: hostEl
      });

      // Set inputs with loading state
      ref.setInput('open', true);
      ref.setInput('timesheet', dummyTimesheet);
      ref.setInput('isLoadingInitialEntry', true);

      // Attach the view to the Angular application
      this.appRef.attachView(ref.hostView);
      ref.changeDetectorRef.detectChanges();

      // Helper function to close the dialog and clean up resources
      const closeDialog = (result?: 'rejected' | 'approved'): void => {
        if (settled) return;

        settled = true;
        resolve(result);

        // Clean up: detach view, destroy component, remove DOM element
        this.appRef.detachView(ref.hostView);
        ref.destroy();
        hostEl.remove();
      };

      // Listen to the modal's closed output event
      ref.instance.closed.subscribe((result) => {
        closeDialog(result);
      });

      // Fetch the actual timesheet data
      lastValueFrom(this.timesheetService.getEntry(entryId))
        .then((timesheet) => {
          if (!timesheet) {
            throw new Error('Failed to load timesheet entry');
          }
          // Update the modal with actual data
          ref.setInput('timesheet', timesheet);
          ref.setInput('isLoadingInitialEntry', false);
          ref.changeDetectorRef.detectChanges();
        })
        .catch((error) => {
          // Show error toast
          const errorMessage =
            error instanceof HttpErrorResponse
              ? error.error?.message || 'Failed to load timesheet entry'
              : error instanceof Error
                ? error.message
                : 'An unexpected error occurred';

          this.toastService.openToast({
            type: 'Error',
            title: errorMessage
          });

          // Close the modal on error
          closeDialog(undefined);
        });
    });
  }
}
